import {ActionTree, GetterTree, Module, MutationTree} from 'vuex';
import {RootState} from '../RootState';
import axios from "axios";
import {settings} from "@/plugins/settings";
import {IErrors} from "@/model/IErrors";
import {ILocker} from "@/model/Locker/lockerInterfaces";
import responseHandler from "@/plugins/errorHandling";

export interface IUserImported{
  id?: number|null,
  line: number|null,
  code_import: number,
  email: string,
  first_name: string,
  last_name: string,
  locker_id: number,
  phone_number: string;
  deliveryToken?: string|null;
  locker_name?: string|null;
  community_id?: number|null;
  community_name?: string|null;
  // 101 'invited' (normal mode): whether the invitation email went out (false outside production)
  invitation_sent?: boolean|null;
  // override mode, existing users (102/103/106): whether the csv name / surname were applied
  name_updated?: boolean|null;
  // 102: where the user was before being moved to this locker
  previous_locker_id?: number|null;
  previous_locker_name?: string|null;
  previous_community_id?: number|null;
  previous_community_name?: string|null;
  // short stable key explaining ambiguous codes (104, 10, 152...)
  detail?: string|null;
  active_orders_count?: number|null;
}

export interface IDiffusionExpressOptions {
  file: File,
  lockerId: number,
  languageCode: string,
  ignorePhone: boolean,
  overrideLockerUsers: boolean,
}

const buildFormData = (options: IDiffusionExpressOptions): FormData => {
  const formData = new FormData();
  formData.set('languageCode', options.languageCode);
  formData.set('ignorePhone', options.ignorePhone ? '1' : '0');
  formData.set('overrideLockerUsers', options.overrideLockerUsers ? '1' : '0');
  formData.append('csv', options.file);
  return formData;
};

interface UserDiffusionExpressState {
  importResults: IUserImported[];
  importPreview: IUserImported[];
  importLockers: ILocker[];
  errors: IErrors;
}

const state: UserDiffusionExpressState = {
  importLockers: [],
  importResults: [],
  importPreview: [],
  errors: {},
};

const getters: GetterTree<UserDiffusionExpressState, RootState> = {
  importLockers: (state): ILocker[] => state.importLockers,
  importResults: (state) => state.importResults,
  importPreview: (state) => state.importPreview,
  errors: (state) => state.errors,
};

const actions: ActionTree<UserDiffusionExpressState, RootState> = {
  fetchImportLockers: ({commit}): any => responseHandler.handle(
    () => axios({
      url: settings.endpoint + 'users/diffusionExpress/lockers',
      method: 'GET'
    }),
    commit,
    'importLockersLoaded'
  ),
  importUsers: ({ commit }, options: IDiffusionExpressOptions): any => {
    commit('usersImported', []);

    return responseHandler.handle(
      () => axios({
        url: settings.endpoint + 'users/diffusionExpress/' + options.lockerId,
        method: 'POST',
        data: buildFormData(options),
        headers: {
          'Content-Type': 'multipart/form-data'
        },
      }),
      commit,
      'usersImported'
    )
  },
  previewImport: ({ commit }, options: IDiffusionExpressOptions): any => {
    commit('importPreviewLoaded', []);

    return responseHandler.handle(
      () => axios({
        url: settings.endpoint + 'users/diffusionExpress/' + options.lockerId + '/preview',
        method: 'POST',
        data: buildFormData({...options, overrideLockerUsers: true}),
        headers: {
          'Content-Type': 'multipart/form-data'
        },
      }),
      commit,
      'importPreviewLoaded'
    )
  },
  exportUsers: ({ commit }): any => {
    return responseHandler.handleDownload(
      () => axios({
        url: settings.endpoint + 'users/export.csv',
        method: 'GET',
        responseType: 'blob',
      }),
      commit,
      'exportUsersLoaded',
      'users.csv'
    )
  },
  errors: ({commit}, errors: IErrors): any => {
    commit('errors', errors);
  },
};

const mutations: MutationTree<UserDiffusionExpressState> = {
  importLockersLoaded(state, payload) {
    state.importLockers = payload;
  },
  exportUsersLoaded() {
    // do nothing, needed to keep commit() and trigger event correctly
  },
  usersImported(state, payload) {
    state.importResults = payload;
  },
  importPreviewLoaded(state, payload) {
    state.importPreview = payload;
  },
  errors(state, payload: IErrors) {
    state.errors = payload;
  },
};

const namespaced: boolean = true;

export const userDiffusionExpressModule: Module<UserDiffusionExpressState, RootState> = {
  namespaced,
  state,
  getters,
  actions,
  mutations
};
